/**
 * Centralized error messages for consistent error handling across the application
 */

export const ERROR_MESSAGES = {
  // System errors
  SYSTEM_ERROR: "ระบบขัดข้อง ขออภัยในความไม่สะดวก",
  NETWORK_ERROR: "ไม่สามารถเชื่อมต่อเครือข่ายได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต",
  NETWORK_ERROR_EN: "No internet connection. Please check your network.",
  SERVER_ERROR: "เซิร์ฟเวอร์ขัดข้อง กรุณาลองใหม่อีกครั้ง",
  TIMEOUT_ERROR: "หมดเวลาการเชื่อมต่อ กรุณาลองใหม่อีกครั้ง",
  
  // Authentication errors
  INVALID_CREDENTIALS: "อีเมล หรือ รหัสผ่านไม่ถูกต้อง",
  INVALID_CREDENTIALS_CHECK: "กรุณาตรวจสอบ อีเมลล์ หรือ รหัสผ่าน ให้ถูกต้อง",
  SESSION_EXPIRED: "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่",
  UNAUTHORIZED: "ไม่มีสิทธิ์เข้าถึงข้อมูล",
  TOKEN_EXPIRED: "Token หมดอายุ กรุณาเข้าสู่ระบบใหม่",
  
  // OAuth2 specific errors
  OAUTH_SYSTEM_ERROR: "เกิดข้อผิดพลาดในระบบ",
  OAUTH_CONSENT_ERROR: "ไม่สามารถให้สิทธิ์การเข้าถึงได้",
  OAUTH_CLIENT_ERROR: "ไม่สามารถโหลดข้อมูลแอพพลิเคชันได้",
  
  // Validation errors
  VALIDATION_ERROR: "ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบอีกครั้ง",
  REQUIRED_FIELD: "กรุณากรอกข้อมูลให้ครบถ้วน",
  INVALID_FORMAT: "รูปแบบข้อมูลไม่ถูกต้อง",
  
  // Business logic errors
  OPERATION_FAILED: "ไม่สามารถดำเนินการได้ กรุณาลองใหม่อีกครั้ง",
  DATA_NOT_FOUND: "ไม่พบข้อมูลที่ต้องการ",
  PERMISSION_DENIED: "ไม่มีสิทธิ์ในการดำเนินการนี้",
  
  // Generic fallback
  UNKNOWN_ERROR: "เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ"
};

export const ERROR_TYPES = {
  NETWORK: 'network',
  AUTH: 'auth',
  SERVER: 'server',
  VALIDATION: 'validation',
  BUSINESS: 'business',
  UNKNOWN: 'unknown'
};

/**
 * Get appropriate error message based on error object
 * @param {Error} error - The error object
 * @returns {string} - Localized error message
 */
export const getErrorMessage = (error) => {
  if (!error) return ERROR_MESSAGES.UNKNOWN_ERROR;
  
  // Network errors
  if (!navigator.onLine || error.message === "Network Error") {
    return ERROR_MESSAGES.NETWORK_ERROR;
  }
  
  // Check if error has response (HTTP error)
  if (error.response) {
    const { status, data } = error.response;
    const errorCode = data?.errorCode;
    const errorMessage = data?.errorMessage;
    
    switch (status) {
      case 401:
        return ERROR_MESSAGES.UNAUTHORIZED;
      case 403:
        return ERROR_MESSAGES.PERMISSION_DENIED;
      case 404:
        return ERROR_MESSAGES.DATA_NOT_FOUND;
      case 422:
        return ERROR_MESSAGES.VALIDATION_ERROR;
      case 500:
        return ERROR_MESSAGES.SERVER_ERROR;
      case 503:
        return ERROR_MESSAGES.SERVER_ERROR;
      case 504:
        return ERROR_MESSAGES.TIMEOUT_ERROR;
      default:
        // Return server message if available, otherwise generic error
        return errorMessage || ERROR_MESSAGES.SYSTEM_ERROR;
    }
  }
  
  // Fallback to generic system error
  return ERROR_MESSAGES.SYSTEM_ERROR;
};

/**
 * Determine error type for categorization
 * @param {Error} error - The error object
 * @returns {string} - Error type
 */
export const getErrorType = (error) => {
  if (!error) return ERROR_TYPES.UNKNOWN;
  
  if (!navigator.onLine || error.message === "Network Error") {
    return ERROR_TYPES.NETWORK;
  }
  
  if (error.response) {
    const { status } = error.response;
    
    if (status === 401 || status === 403) {
      return ERROR_TYPES.AUTH;
    }
    
    if (status >= 500) {
      return ERROR_TYPES.SERVER;
    }
    
    if (status === 422) {
      return ERROR_TYPES.VALIDATION;
    }
    
    return ERROR_TYPES.BUSINESS;
  }
  
  return ERROR_TYPES.UNKNOWN;
};